/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 */
export const queryKeys = {
  // ── Chronos (time tracking). Arrays preserve the historical literals so as not to
  //    invalidate shared caches (e.g. projects-list is intentionally used in 2 views).
  chronos: {
    all: ["chronos"] as const,
    myWorkspaces: (userId: string | undefined) => ["chronos", "my-workspaces", userId] as const,
    workspaceTags: (workspaceId: string | undefined) =>
      ["chronos", "workspace-tags", workspaceId] as const,
    /** Projects in the workspace (timer selector). */
    projects: (workspaceId: string | undefined) => ["chronos", "projects", workspaceId] as const,
    /** Project list (projects + timeline views SHARE the cache: same query). */
    projectsList: (userId: string | undefined, workspaceId: string | undefined) =>
      ["chronos", "projects-list", userId, workspaceId] as const,
    projectMembers: (userId: string | undefined) =>
      ["chronos", "project-members-list", userId] as const,
    projectTasks: (projectId: string) => ["chronos", "project-tasks", projectId] as const,
    /** Project details by id (not-found/no-access resolution). */
    projectDetail: (projectId: string) => ["chronos", "project-detail", projectId] as const,
    /** LOCAL version history for a project (bounded bucket). */
    projectVersions: (projectId: string | undefined) =>
      ["chronos", "project-versions", projectId] as const,
    /** All local versions for the user (timesheet: timestamp per block). */
    projectVersionsAll: (userId: string | undefined) =>
      ["chronos", "project-versions-all", userId] as const,
    /** SEALED version of a time_entry (API fetch for the timesheet). */
    sealedVersion: (timeEntryId: string | undefined) =>
      ["chronos", "sealed-version", timeEntryId] as const,
    /** Gamification: user's completed blocks (XP/streak/badges). */
    gamificationEntries: (userId: string | undefined) =>
      ["chronos", "gamification-entries", userId] as const,
    /** Gamification: user's completed focus sessions. */
    gamificationFocus: (userId: string | undefined) =>
      ["chronos", "gamification-focus", userId] as const,
    timerTasks: (projectId: string | undefined) => ["chronos", "timer-tasks", projectId] as const,
    lastEntry: (userId: string | undefined) => ["chronos", "lastEntry", userId] as const,
    activeTimer: (userId: string | undefined) => ["chronos", "activeTimer", userId] as const,
    pomodoroConfig: (userId: string | undefined) => ["chronos", "pomodoro-config", userId] as const,
    dashboardProjects: (userId: string | undefined, workspaceId: string | undefined) =>
      ["chronos", "dashboard-projects-list", userId, workspaceId] as const,
    dashboardWeeklyEntries: (userId: string | undefined) =>
      ["chronos", "dashboard-weekly-entries", userId] as const,
    weeklyEntriesProjects: (userId: string | undefined) =>
      ["chronos", "weekly-entries-projects", userId] as const,
    deepWork: {
      pomodoroConfig: (userId: string | undefined) =>
        ["chronos", "deep-work", "pomodoro-config", userId] as const,
      activeSession: (userId: string | undefined) =>
        ["chronos", "deep-work", "active-session", userId] as const,
      sessions: (userId: string | undefined) =>
        ["chronos", "deep-work", "sessions", userId] as const,
    },
    team: {
      members: (userId: string | undefined, workspaceId: string | undefined) =>
        ["chronos", "team", "members", userId, workspaceId] as const,
      feedEvents: (userId: string | undefined, workspaceId: string | undefined) =>
        ["chronos", "team", "feed-events", userId, workspaceId] as const,
      reactions: (userId: string | undefined) => ["chronos", "team", "reactions", userId] as const,
    },
    analytics: {
      timeEntries: (userId: string | undefined) =>
        ["chronos", "analytics", "time-entries", userId] as const,
      rawAppLogs: (userId: string | undefined) =>
        ["chronos", "analytics", "raw-app-logs", userId] as const,
      projects: (userId: string | undefined, workspaceId: string | undefined) =>
        ["chronos", "analytics-projects", userId, workspaceId] as const,
    },
    timeline: {
      rawAppLogs: (userId: string | undefined) => ["chronos", "raw-app-logs", userId] as const,
      timeEntriesHistory: (userId: string | undefined) =>
        ["chronos", "time-entries-history", userId] as const,
      tasks: (userId: string | undefined) => ["chronos", "timeline-tasks", userId] as const,
      tags: (workspaceId: string | undefined) => ["chronos", "timeline-tags", workspaceId] as const,
      entryTags: (userId: string | undefined) =>
        ["chronos", "timeline-entry-tags", userId] as const,
      feedEvents: (userId: string | undefined) =>
        ["chronos", "timeline-feed-events", userId] as const,
    },
  },
  // ── ACTIVITY-CORE (history/feed/trash). Always with userId → isolates the cache
  //    between accounts on the same browser.
  activity: {
    all: ["activity"] as const,
    feed: (userId: string | undefined, workspaceId: string | undefined) =>
      ["activity", "feed", userId, workspaceId] as const,
    entity: (userId: string | undefined, entityType: string, entityId: string) =>
      ["activity", "entity", userId, entityType, entityId] as const,
    trashEntries: (userId: string | undefined, workspaceId: string | undefined) =>
      ["activity", "trash-entries", userId, workspaceId] as const,
    trashProjects: (userId: string | undefined, workspaceId: string | undefined) =>
      ["activity", "trash-projects", userId, workspaceId] as const,
  },
  search: {
    all: ["search"] as const,
    results: (query: string, filter: string) => ["search", "results", query, filter] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Full theme catalog (profile). */
    catalog: () => ["themes", "catalog"] as const,
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    userTheme: (userId: string | undefined) => ["themes", "userTheme", userId] as const,
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  powersync: {
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all? */
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
    /**
     * List of devices with an open session.
     *
     * The PREFIX (without `sessionId`) is what `BootManager`'s `onRevokeSuccess`
     * invalidates after revocation: the list lives in this query, and without
     * invalidating it, the screen kept showing a device that had already been removed
     * (P23/CC-60 · P25). It was written as an inline literal in two places, which
     * is precisely what prevents guaranteed invalidation.
     */
    activeSessions: () => ["active-sessions"] as const,
    activeSessionsFor: (currentSessionId: string | undefined) =>
      ["active-sessions", currentSessionId] as const,
  },
};
